import React, { useState } from 'react';
import { useGetUsersQuery } from '../store/adminApi';
import DataTable from './DataTable';
import Pagination from './Pagination';
import { User, UserRole } from '../types';

const ITEMS_PER_PAGE = 10;

const UserList: React.FC = () => {
  const { data: users = [], error, isLoading } = useGetUsersQuery();
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');

  const filtered = users.filter(u =>
    u.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE);
  const paginated = filtered.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);

  const roleBadge = (role: UserRole) => {
    if (role === 'superadmin') return <span className="badge-purple">Super Admin</span>;
    if (role === 'admin') return <span className="badge-orange">Admin</span>;
    return <span className="badge-blue">User</span>;
  };

  if (error) {
    return (
      <div className="text-center py-10 text-red-500">
        <p className="font-semibold">Error fetching users.</p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
        <div>
          <h2 className="font-display font-bold text-xl text-trident-gray-900">Users</h2>
          <p className="text-sm text-trident-gray-500">Total: {users.length} users</p>
        </div>
        <div className="relative">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 text-trident-gray-400" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
          <input
            type="text"
            value={searchTerm}
            onChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }}
            placeholder="Search by name or email..."
            className="input-field pl-10 sm:w-72"
          />
        </div>
      </div>

      <DataTable
        loading={isLoading}
        rows={paginated}
        rowKey={(u) => u._id}
        emptyMessage={searchTerm ? `No users match "${searchTerm}"` : 'No users yet'}
        columns={[
          {
            key: 'name', label: 'Name',
            render: (u: User) => (
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-trident-gradient flex items-center justify-center text-white font-display font-bold text-xs shadow-trident">
                  {u.name.split(' ').filter(Boolean).map(w => w[0].toUpperCase()).slice(0, 2).join('') || 'U'}
                </div>
                <div>
                  <div className="font-semibold text-trident-gray-900">{u.name}</div>
                  <div className="text-xs text-trident-gray-500">{u.designation}</div>
                </div>
              </div>
            ),
          },
          { key: 'email', label: 'Email' },
          { key: 'role', label: 'Role', render: (u) => roleBadge(u.role) },
          {
            key: 'verified', label: 'Verified', align: 'center',
            render: (u) => u.isVerified
              ? <span className="badge-green">✓ Yes</span>
              : <span className="badge-gray">Pending</span>,
          },
        ]}
      />

      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        totalItems={filtered.length}
        itemsPerPage={ITEMS_PER_PAGE}
        onPageChange={setCurrentPage}
      />
    </div>
  );
};

export default UserList;
